import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Plus,
  Wifi,
  X,
  HelpCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  Upload,
  ImageIcon,
  AlertCircle,
  Link2,
  ClipboardCheck,
  ShoppingBag,
  GripVertical,
  Phone,
  RefreshCw,
  Building2,
  Plug,
  FileText,
  LayoutGrid,
  MessageSquare,
  CreditCard,
  ChevronRight,
  Lock,
  Webhook,
  Copy,
  RotateCcw,
} from 'lucide-react'
import { ERP_META, type ErpHelpEntry, type Step } from './erpHelpMeta'
import { ErpAccordionItem } from './AdminHelpTooltips'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import type { AdminERP, PAProvider } from '../../types'
import PasswordInput from '../../components/PasswordInput'

// ─── Constants ───────────────────────────────────────────────────────────────

const ERP_TYPES: { value: string; label: string; logo?: string }[] = [
  { value: 'dolibarr', label: 'Dolibarr' },
  { value: 'salesforce', label: 'Salesforce' },
  { value: 'bleez', label: 'Bleez' },
  { value: 'business_central', label: 'Microsoft Business Central' },
  { value: 'odoo', label: 'Odoo' },
  { value: 'sage100', label: 'Sage 100' },
  { value: 'netsuite', label: 'NetSuite (Oracle)' },
  { value: 'divalto', label: 'Divalto Infinity' },
  { value: 'erpnext', label: 'ERPNext / Frappe' },
  { value: 'zoho', label: 'Zoho One' },
  { value: 'cegid', label: 'Cegid XRP Flex' },
  { value: 'csv', label: 'CSV / Excel' },
  { value: 'custom', label: 'Autre / Personnalisé' },
]

const ALL_MODULES: { key: string; labelKey: string }[] = [
  { key: 'invoices', labelKey: 'nav.invoices' },
  { key: 'orders', labelKey: 'nav.orders' },
  { key: 'proposals', labelKey: 'nav.proposals' },
  { key: 'catalog', labelKey: 'nav.catalog' },
  { key: 'shipments', labelKey: 'nav.shipments' },
  { key: 'tickets', labelKey: 'nav.tickets' },
  { key: 'projects', labelKey: 'nav.projects' },
  { key: 'gallery', labelKey: 'nav.gallery' },
  { key: 'notifications', labelKey: 'nav.registrations' },
]

const defaultModules: Record<string, boolean> = {
  invoices: true,
  orders: true,
  shipments: true,
  tickets: true,
  catalog: true,
  gallery: true,
  proposals: false,
  projects: false,
  notifications: false,
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface ChatbotQuestion {
  id: string
  label: string
  type: 'text' | 'date' | 'time' | 'number' | 'email' | 'phone' | 'select' | 'textarea'
  options?: string[]
  required: boolean
  field: 'subject' | 'message' | 'type_code' | 'category_code' | 'severity_code'
}

interface ChatbotConfig {
  enabled: boolean
  welcome_message: string
  questions: ChatbotQuestion[]
}

interface VapiConfig {
  enabled: boolean
  phone_number: string
  phone_number_id: string
  assistant_id: string
  voice_provider: string
  voice_id: string
  language: string
}

interface ERPFormData {
  erp_type: string
  erp_name: string
  erp_description: string
  siren: string
  siret: string
  vat_number: string
  iban: string
  bic: string
  pa_provider: string
  pa_config: Record<string, string>
  website: string
  base_url: string
  api_key: string
  api_secret: string
  api_version: string
  is_active: boolean
  shop_enabled: boolean
  shop_show_prices: boolean
  bc_tenant_id: string
  bc_environment: string
  bc_company_id: string
  bleez_username: string
  bleez_password: string
  bleez_cle_encryptee: string
  stt_vocabulary: string
  chatbot_config: ChatbotConfig
  vapi_config: VapiConfig
}

const emptyForm: ERPFormData = {
  erp_type: 'dolibarr',
  erp_name: '',
  erp_description: '',
  siren: '',
  siret: '',
  vat_number: '',
  iban: '',
  bic: '',
  pa_provider: '',
  pa_config: {},
  website: '',
  base_url: '',
  api_key: '',
  api_secret: '',
  api_version: '',
  is_active: true,
  shop_enabled: false,
  shop_show_prices: true,
  bc_tenant_id: '',
  bc_environment: 'production',
  bc_company_id: '',
  bleez_username: '',
  bleez_password: '',
  bleez_cle_encryptee: '',
  stt_vocabulary: '',
  chatbot_config: { enabled: false, welcome_message: '', questions: [] },
  vapi_config: {
    enabled: false,
    phone_number: '',
    phone_number_id: '',
    assistant_id: '',
    voice_provider: 'azure',
    voice_id: '',
    language: 'fr',
  },
}

// ─── Field config per ERP type ────────────────────────────────────────────────

const getFieldLabels = (erpType: string, t: (key: string) => string) => {
  switch (erpType) {
    case 'dolibarr':
      return {
        base_url: t('admin.erps.fields.dolibarr.base_url'),
        base_url_placeholder: 'https://mon-dolibarr.example.com',
        base_url_help: t('admin.erps.fields.dolibarr.base_url_help'),
        show_api_key: true,
        api_key: t('admin.erps.fields.dolibarr.api_key'),
        api_key_help: t('admin.erps.fields.dolibarr.api_key_help'),
        show_api_secret: false,
        api_secret: '',
        api_secret_help: '',
        show_api_version: false,
        api_version: '',
        api_version_help: '',
      }
    case 'salesforce':
      return {
        base_url: t('admin.erps.fields.salesforce.base_url'),
        base_url_placeholder: 'https://mycompany.salesforce.com',
        base_url_help: t('admin.erps.fields.salesforce.base_url_help'),
        show_api_key: true,
        api_key: t('admin.erps.fields.salesforce.api_key'),
        api_key_help: t('admin.erps.fields.salesforce.api_key_help'),
        show_api_secret: true,
        api_secret: t('admin.erps.fields.salesforce.api_secret'),
        api_secret_help: t('admin.erps.fields.salesforce.api_secret_help'),
        show_api_version: true,
        api_version: t('admin.erps.fields.salesforce.api_version'),
        api_version_help: t('admin.erps.fields.salesforce.api_version_help'),
      }
    case 'bleez':
      return {
        base_url: t('admin.erps.fields.bleez.base_url'),
        base_url_placeholder: 'https://api.bleez.com',
        base_url_help: t('admin.erps.fields.bleez.base_url_help'),
        show_api_key: true,
        api_key: t('admin.erps.fields.bleez.api_key'),
        api_key_help: t('admin.erps.fields.bleez.api_key_help'),
        show_api_secret: true,
        api_secret: t('admin.erps.fields.bleez.api_secret'),
        api_secret_help: t('admin.erps.fields.bleez.api_secret_help'),
        show_api_version: false,
        api_version: '',
        api_version_help: '',
      }
    case 'business_central':
      return {
        base_url: t('admin.erps.fields.business_central.base_url'),
        base_url_placeholder: 'https://api.businesscentral.dynamics.com/v2.0',
        base_url_help: t('admin.erps.fields.business_central.base_url_help'),
        show_api_key: true,
        api_key: t('admin.erps.fields.business_central.api_key'),
        api_key_help: t('admin.erps.fields.business_central.api_key_help'),
        show_api_secret: true,
        api_secret: t('admin.erps.fields.business_central.api_secret'),
        api_secret_help: t('admin.erps.fields.business_central.api_secret_help'),
        show_api_version: true,
        api_version: t('admin.erps.fields.common.api_version'),
        api_version_help: t('admin.erps.fields.business_central.api_version_help'),
      }
    case 'odoo':
      return {
        base_url: t('admin.erps.fields.odoo.base_url'),
        base_url_placeholder: 'http://localhost:8069',
        base_url_help: t('admin.erps.fields.odoo.base_url_help'),
        show_api_key: true,
        api_key: t('admin.erps.fields.odoo.api_key'),
        api_key_help: t('admin.erps.fields.odoo.api_key_help'),
        show_api_secret: true,
        api_secret: t('admin.erps.fields.odoo.api_secret'),
        api_secret_help: t('admin.erps.fields.odoo.api_secret_help'),
        show_api_version: true,
        api_version: t('admin.erps.fields.common.api_version'),
        api_version_help: t('admin.erps.fields.odoo.api_version_help'),
      }
    case 'csv':
      return {
        base_url: t('admin.erps.fields.csv.base_url'),
        base_url_placeholder: '/chemin/vers/data',
        base_url_help: t('admin.erps.fields.csv.base_url_help'),
        show_api_key: false,
        api_key: '',
        api_key_help: '',
        show_api_secret: false,
        api_secret: '',
        api_secret_help: '',
        show_api_version: false,
        api_version: '',
        api_version_help: '',
      }
    default:
      return {
        base_url: t('admin.erps.fields.default.base_url'),
        base_url_placeholder: 'https://erp.example.com',
        base_url_help: t('admin.erps.fields.default.base_url_help'),
        show_api_key: true,
        api_key: t('admin.erps.fields.common.api_key'),
        api_key_help: t('admin.erps.fields.default.api_key_help'),
        show_api_secret: true,
        api_secret: t('admin.erps.fields.common.api_secret'),
        api_secret_help: t('admin.erps.fields.default.api_secret_help'),
        show_api_version: true,
        api_version: t('admin.erps.fields.common.api_version'),
        api_version_help: '',
      }
  }
}

// ─── HelpTip ─────────────────────────────────────────────────────────────────

function HelpTip({ text }: { text: string }) {
  const [show, setShow] = useState(false)
  const iconRef = useRef<HTMLSpanElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; showBelow: boolean } | null>(null)

  if (!text) return null

  const updatePos = () => {
    if (!iconRef.current) return
    const rect = iconRef.current.getBoundingClientRect()
    const showBelow = rect.top < 120
    setPos({
      top: showBelow ? rect.bottom + 6 : rect.top - 6,
      left: rect.left + rect.width / 2,
      showBelow,
    })
  }

  return (
    <span className="inline-block ml-1 align-middle" ref={iconRef}>
      <HelpCircle
        className="w-4 h-4 text-gray-400 hover:text-primary-500 cursor-help inline"
        onMouseEnter={() => {
          updatePos()
          setShow(true)
        }}
        onMouseLeave={() => setShow(false)}
        onClick={() => {
          updatePos()
          setShow(!show)
        }}
      />
      {show && pos && (
        <span
          className="fixed w-72 p-3 text-xs text-white bg-gray-800 rounded-xl shadow-xl pointer-events-none leading-relaxed"
          style={{
            zIndex: 9999,
            left: pos.left,
            top: pos.showBelow ? pos.top : undefined,
            bottom: pos.showBelow ? undefined : window.innerHeight - pos.top,
            transform: 'translateX(-50%)',
          }}
        >
          {text}
          <span
            className={`absolute left-1/2 -translate-x-1/2 border-4 border-transparent ${pos.showBelow ? 'bottom-full border-b-gray-800' : 'top-full border-t-gray-800'}`}
          />
        </span>
      )}
    </span>
  )
}

// ─── Stripe section ───────────────────────────────────────────────────────────

function StripeConfigSection({ erpId, isSuperAdmin }: { erpId: number; isSuperAdmin: boolean }) {
  const { t } = useTranslation()
  const [cfg, setCfg] = useState<import('../../types').StripeConfig>({
    enabled: false,
    currency: 'EUR',
    recurring_enabled: false,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [onboarding, setOnboarding] = useState(false)
  const [accountStatus, setAccountStatus] = useState<'not_connected' | 'pending' | 'active' | null>(
    null
  )

  const loadConfig = () => {
    if (!erpId) return
    setLoading(true)
    api
      .getStripeConfig(erpId)
      .then((d) => {
        setCfg({ ...{ enabled: false, currency: 'EUR', recurring_enabled: false }, ...d })
        if (d?.stripe_account_id) {
          return api
            .getStripeAccountStatus(erpId)
            .then((s) => setAccountStatus(s.status))
            .catch(() => {})
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadConfig()
  }, [erpId]) // eslint-disable-line

  const handleSave = async () => {
    if (!isSuperAdmin) return
    setSaving(true)
    try {
      await api.saveStripeConfig(erpId, cfg)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch {
      /* ignore */
    } finally {
      setSaving(false)
    }
  }

  const handleOnboarding = async () => {
    setOnboarding(true)
    try {
      const res = await api.startStripeOnboarding(erpId)
      window.location.href = res.url
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      alert(apiErr?.response?.data?.error ?? 'Erreur lors de la connexion Stripe')
    } finally {
      setOnboarding(false)
    }
  }

  if (loading)
    return <p className="text-xs text-gray-400">{t('common.loading', 'Chargement...')}</p>

  const isConnected = !!cfg.stripe_account_id

  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={cfg.enabled}
          onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
          disabled={!isSuperAdmin}
          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
        />
        <span className="text-sm text-gray-700">
          {t('admin.erps.stripeEnabled', 'Activer le paiement Stripe Connect')}
        </span>
      </label>
      {cfg.enabled && (
        <>
          <div className="rounded-lg border border-gray-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {t('admin.erps.stripeConnectAccount', 'Compte Stripe Connect Express')}
                </p>
                {isConnected && (
                  <p className="text-xs text-gray-400 font-mono mt-0.5">{cfg.stripe_account_id}</p>
                )}
              </div>
              {accountStatus === 'active' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                  ✓ {t('admin.erps.stripeActive', 'Actif')}
                </span>
              )}
              {(accountStatus === 'not_connected' || !isConnected) && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                  {t('admin.erps.stripeNotConnected', 'Non connecté')}
                </span>
              )}
            </div>
            {isSuperAdmin && (
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleOnboarding}
                  disabled={onboarding}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium rounded-lg disabled:opacity-50"
                >
                  {onboarding && <Loader2 size={12} className="animate-spin" />}
                  {isConnected
                    ? t('admin.erps.stripeResumeOnboarding', "Reprendre l'onboarding")
                    : t('admin.erps.stripeStartOnboarding', 'Connecter ce fournisseur')}
                </button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {t('admin.erps.stripeCurrency', 'Devise')}
              </label>
              <select
                value={cfg.currency}
                disabled={!isSuperAdmin}
                onChange={(e) => setCfg({ ...cfg, currency: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
              >
                <option value="EUR">EUR €</option>
                <option value="USD">USD $</option>
                <option value="GBP">GBP £</option>
              </select>
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!cfg.recurring_enabled}
                  disabled={!isSuperAdmin}
                  onChange={(e) => setCfg({ ...cfg, recurring_enabled: e.target.checked })}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-xs text-gray-700">
                  {t('admin.erps.stripeRecurring', 'Paiements récurrents')}
                </span>
              </label>
            </div>
          </div>
        </>
      )}
      {isSuperAdmin && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('common.save', 'Enregistrer')}
          </button>
          {saved && (
            <span className="text-sm text-green-600">{t('common.saved', 'Enregistré !')}</span>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Webhook config section ───────────────────────────────────────────────────
// Shows the ERP webhook URL + secret and a step-by-step Dolibarr setup guide.
// The secret is masked by default — the admin can reveal/copy it.
// Regenerating invalidates the old secret; Dolibarr must be updated accordingly.
function WebhookConfigSection({ erpId }: { erpId: number }) {
  const { t } = useTranslation()
  const [config, setConfig] = useState<{ webhook_url: string; secret: string } | null>(null)
  const [loading, setLoading] = useState(true)
  const [regenerating, setRegenerating] = useState(false)
  const [copied, setCopied] = useState<'url' | null>(null)

  useEffect(() => {
    api
      .getERPWebhookConfig(erpId)
      .then(setConfig)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [erpId])

  const copy = (value: string, type: 'url') => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    })
  }

  const handleRegenerate = async () => {
    if (!window.confirm(t('webhookConfig.regenerateConfirm'))) return
    setRegenerating(true)
    try {
      await api.regenerateERPWebhookSecret(erpId)
      // Re-fetch the full config so the URL (which contains the token) is also updated
      const fresh = await api.getERPWebhookConfig(erpId)
      setConfig(fresh)
    } catch {
    } finally {
      setRegenerating(false)
    }
  }

  if (loading)
    return (
      <div className="flex justify-center py-6">
        <RefreshCw size={18} className="animate-spin text-gray-400" />
      </div>
    )

  return (
    <div className="space-y-6">
      {/* URL + regenerate */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('webhookConfig.urlLabel')}
        </label>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={config?.webhook_url ?? ''}
            className="flex-1 font-mono text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 px-3 py-2"
          />
          <button
            onClick={() => copy(config?.webhook_url ?? '', 'url')}
            className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title={t('common.copy')}
          >
            {copied === 'url' ? (
              <CheckCircle2 size={16} className="text-green-500" />
            ) : (
              <Copy size={16} className="text-gray-500" />
            )}
          </button>
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            className="p-2 rounded-lg border border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors text-amber-600"
            title={t('webhookConfig.regenerate')}
          >
            <RotateCcw size={16} className={regenerating ? 'animate-spin' : ''} />
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1">{t('webhookConfig.urlHint')}</p>
      </div>

      {/* Dolibarr setup guide */}
      <div className="border border-blue-200 dark:border-blue-800 rounded-xl bg-blue-50 dark:bg-blue-900/20 p-4">
        <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-3 flex items-center gap-2">
          <HelpCircle size={15} />
          {t('webhookConfig.guideTitle')}
        </h4>
        <ol className="space-y-2 text-xs text-blue-700 dark:text-blue-300 list-decimal list-inside">
          {(t('webhookConfig.guideSteps', { returnObjects: true }) as string[]).map(
            (step: string, i: number) => (
              <li key={i}>{step}</li>
            )
          )}
        </ol>

        <div className="mt-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
            {t('webhookConfig.eventsLabel')}
          </p>
          <ul className="text-xs font-mono text-gray-700 dark:text-gray-300 space-y-1">
            {(
              [
                'eventInvoice',
                'eventInvoiceModify',
                'eventProposal',
                'eventProposalSent',
                'eventProposalModify',
                'eventSigned',
                'eventRefused',
              ] as const
            ).map((key) => (
              <li key={key} className="flex items-start gap-1">
                <span className="w-1 h-1 rounded-full bg-blue-400 flex-shrink-0 mt-1.5" />
                {t(`webhookConfig.${key}`)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({
  id,
  icon: Icon,
  title,
  subtitle,
  children,
}: {
  id: string
  icon: React.ElementType
  title: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <div
      id={id}
      className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden scroll-mt-6"
    >
      <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary-100">
          <Icon className="w-5 h-5 text-primary-600" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="p-6 space-y-5">{children}</div>
    </div>
  )
}

// ─── Field wrapper ────────────────────────────────────────────────────────────

function Field({
  label,
  hint,
  help,
  required,
  children,
}: {
  label: string
  hint?: string
  help?: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
        {help && <HelpTip text={help} />}
      </label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminERPForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const editingId = id ? parseInt(id) : null

  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') ?? '{}')
    } catch {
      return {}
    }
  })()
  const isSuperAdmin = currentUser?.role === 'super_admin'

  const [form, setForm] = useState<ERPFormData>(emptyForm)
  const [modulesEnabled, setModulesEnabled] = useState<Record<string, boolean>>(defaultModules)
  const [modulesActive, setModulesActive] = useState<Record<string, boolean>>(
    Object.fromEntries(Object.keys(defaultModules).map((k) => [k, true]))
  )
  const [lastTestModules, setLastTestModules] = useState<Record<
    string,
    { success: boolean; count?: number; error?: string }
  > | null>(null)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(!!editingId)
  const [error, setError] = useState('')
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [paProviders, setPaProviders] = useState<PAProvider[]>([])
  const [vapiPhoneNumbers, setVapiPhoneNumbers] = useState<
    { id: string; number: string; name: string }[]
  >([])
  const [vapiSyncing, setVapiSyncing] = useState(false)
  const [vapiSyncResult, setVapiSyncResult] = useState<{
    success: boolean
    message: string
  } | null>(null)
  const [copiedSiren, setCopiedSiren] = useState(false)
  const [copiedShop, setCopiedShop] = useState(false)
  const [activeSection, setActiveSection] = useState('general')
  const [testOpen, setTestOpen] = useState(false)
  const [testLoading, setTestLoading] = useState(false)
  const [testResult, setTestResult] = useState<{
    success: boolean
    error?: string
    modules?: Record<string, { success: boolean; count?: number; error?: string }>
  } | null>(null)
  const [paTestLoading, setPaTestLoading] = useState(false)
  const [paTestResult, setPaTestResult] = useState<{ success: boolean; message: string } | null>(
    null
  )

  const logoInputRef = useRef<HTMLInputElement>(null)

  const sections = [
    { id: 'general', icon: Building2, label: t('admin.erps.sectionGeneral') },
    { id: 'connection', icon: Plug, label: t('admin.erps.sectionConnection') },
    ...(isSuperAdmin
      ? [{ id: 'einvoice', icon: FileText, label: t('admin.erps.sectionEinvoiceTitle') }]
      : []),
    { id: 'modules', icon: LayoutGrid, label: t('admin.erps.sectionModules') },
    { id: 'chatbot', icon: MessageSquare, label: t('admin.erps.sectionChatbot') },
    ...(isSuperAdmin
      ? [{ id: 'payment', icon: CreditCard, label: t('admin.erps.sectionPayment') }]
      : []),
    // Webhook section only shown when editing an existing ERP (not on creation form)
    ...(editingId
      ? [{ id: 'webhooks', icon: Webhook, label: t('webhookConfig.sectionTitle') }]
      : []),
  ]

  const buildLoginUrl = (siren: string) => {
    const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '')
    return `${window.location.origin}${base}/login/${siren.replace(/\s+/g, '')}`
  }
  const buildShopUrl = (siren: string) => {
    const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '')
    return `${window.location.origin}${base}/shop/${siren.replace(/\s+/g, '')}`
  }

  useEffect(() => {
    api
      .getPAProviders()
      .then(setPaProviders)
      .catch(() => {})
    loadVapiPhoneNumbers()
    if (editingId) loadERP(editingId)
  }, []) // eslint-disable-line

  const loadERP = async (erpId: number) => {
    try {
      const erps = await api.getAdminERPs()
      const erp: AdminERP | undefined = (Array.isArray(erps) ? erps : []).find(
        (e: AdminERP) => e.id === erpId
      )
      if (!erp) {
        navigate(backUrl)
        return
      }

      const config =
        typeof erp.config === 'string' ? JSON.parse(erp.config || '{}') : (erp.config ?? {})
      const rawChatbot = erp.chatbot_config
        ? typeof erp.chatbot_config === 'string'
          ? JSON.parse(erp.chatbot_config)
          : erp.chatbot_config
        : { enabled: false, welcome_message: '', questions: [] }
      const rawVapi = erp.vapi_config
        ? typeof erp.vapi_config === 'string'
          ? JSON.parse(erp.vapi_config)
          : erp.vapi_config
        : emptyForm.vapi_config

      setForm({
        erp_type: erp.erp_type,
        erp_name: erp.erp_name,
        erp_description: erp.erp_description ?? '',
        siren: erp.siren ?? '',
        siret: erp.siret ?? '',
        vat_number: erp.vat_number ?? '',
        iban: erp.iban ?? '',
        bic: erp.bic ?? '',
        pa_provider: erp.pa_provider ?? '',
        pa_config: (() => {
          const v =
            typeof erp.pa_config === 'string'
              ? erp.pa_config
                ? JSON.parse(erp.pa_config)
                : {}
              : (erp.pa_config ?? {})
          return v && typeof v === 'object' ? v : {}
        })(),
        website: erp.website ?? '',
        base_url: erp.base_url,
        api_key: erp.api_key ?? '',
        api_secret: erp.api_secret ?? '',
        api_version: erp.api_version ?? '',
        is_active: erp.is_active,
        shop_enabled: erp.shop_enabled ?? false,
        shop_show_prices: erp.shop_show_prices ?? true,
        bc_tenant_id: config.tenant_id ?? '',
        bc_environment: config.environment ?? 'production',
        bc_company_id: config.company_id ?? '',
        bleez_username: config.username ?? '',
        bleez_password: config.password ?? '',
        bleez_cle_encryptee: config.cle_encryptee ?? '',
        stt_vocabulary: config.stt_vocabulary ?? '',
        chatbot_config: rawChatbot,
        vapi_config: rawVapi,
      })
      setLogoPreview(erp.logo || null)
      try {
        const erpModules = await api.getERPModules(erpId)
        const enabled: Record<string, boolean> = {}
        const active: Record<string, boolean> = {}
        for (const [name, detail] of Object.entries(erpModules)) {
          enabled[name] = detail.is_enabled
          active[name] = detail.is_active
        }
        setModulesEnabled(enabled)
        setModulesActive(active)
      } catch {
        setModulesEnabled({ ...defaultModules })
        setModulesActive(Object.fromEntries(Object.keys(defaultModules).map((k) => [k, true])))
      }
    } catch {
      navigate(backUrl)
    } finally {
      setLoading(false)
    }
  }

  const loadVapiPhoneNumbers = async () => {
    if (!isSuperAdmin) return
    try {
      const data = await api.getVapiPhoneNumbers()
      setVapiPhoneNumbers(Array.isArray(data) ? data : [])
    } catch {
      setVapiPhoneNumbers([])
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      let erpId = editingId
      const payload: Record<string, unknown> = { ...form }
      const configObj: Record<string, unknown> = {}
      if (form.stt_vocabulary) configObj.stt_vocabulary = form.stt_vocabulary
      if (form.erp_type === 'business_central') {
        configObj.tenant_id = form.bc_tenant_id
        configObj.environment = form.bc_environment
        configObj.company_id = form.bc_company_id
      } else if (form.erp_type === 'bleez') {
        configObj.username = form.bleez_username
        configObj.password = form.bleez_password
        configObj.cle_encryptee = form.bleez_cle_encryptee
        configObj.siret_emetteur = form.siren
      }
      payload.config = JSON.stringify(configObj)
      payload.chatbot_config = JSON.stringify(form.chatbot_config)
      payload.vapi_config = JSON.stringify(form.vapi_config)
      payload.pa_config =
        form.pa_config && Object.keys(form.pa_config).length ? JSON.stringify(form.pa_config) : null
      delete payload.bc_tenant_id
      delete payload.bc_environment
      delete payload.bc_company_id
      delete payload.bleez_username
      delete payload.bleez_password
      delete payload.bleez_cle_encryptee
      delete payload.stt_vocabulary
      payload.logo = logoPreview || ''

      if (editingId) {
        await api.updateAdminERP(editingId, payload)
      } else {
        const created = await api.createAdminERP(payload)
        erpId = created.id
      }
      if (erpId) {
        const modulesPayload: Record<string, { is_active?: boolean; is_enabled: boolean }> = {}
        for (const mod of ALL_MODULES) {
          if (isSuperAdmin) {
            modulesPayload[mod.key] = {
              is_active: modulesActive[mod.key] ?? true,
              is_enabled: modulesEnabled[mod.key] ?? false,
            }
          } else if (modulesActive[mod.key] ?? true) {
            modulesPayload[mod.key] = { is_enabled: modulesEnabled[mod.key] ?? false }
          }
        }
        await api.updateERPModules(erpId, modulesPayload)
      }
      navigate(backUrl)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setError(apiErr.response?.data?.error ?? t('common.error'))
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    if (!editingId) return
    setTestResult(null)
    setLastTestModules(null)
    setTestLoading(true)
    setTestOpen(true)
    try {
      const result = await api.testERPConnection(editingId)
      setTestResult(result)
      if (result.modules && Object.keys(result.modules).length > 0) {
        setLastTestModules(result.modules)
      }
    } catch {
      setTestResult({ success: false, error: t('admin.erps.connectionFail') })
    } finally {
      setTestLoading(false)
    }
  }

  const handlePATest = async () => {
    if (!editingId) return
    setPaTestResult(null)
    setPaTestLoading(true)
    try {
      const result = await api.testPAConnection(editingId, form.pa_provider, form.pa_config)
      setPaTestResult(result)
    } catch {
      setPaTestResult({ success: false, message: t('admin.erps.connectionFail') })
    } finally {
      setPaTestLoading(false)
    }
  }

  const handleVapiSync = async () => {
    if (!editingId) return
    setVapiSyncing(true)
    setVapiSyncResult(null)
    try {
      await api.syncVapiAssistant(editingId)
      setVapiSyncResult({ success: true, message: t('admin.erps.vapiSyncSuccess') })
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setVapiSyncResult({
        success: false,
        message: apiErr.response?.data?.error ?? t('admin.erps.vapiSyncError'),
      })
    } finally {
      setVapiSyncing(false)
    }
  }

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId)
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Scroll-spy: keep the sidebar nav in sync with the section visible at the top of the viewport
  const sectionIds = useMemo(
    () => [
      'general',
      'connection',
      ...(isSuperAdmin ? ['einvoice'] : []),
      'modules',
      'chatbot',
      ...(isSuperAdmin ? ['payment'] : []),
      ...(editingId ? ['webhooks'] : []),
    ],
    [isSuperAdmin, editingId]
  )

  useEffect(() => {
    if (loading) return

    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null)

    if (elements.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting)
        if (visible.length === 0) return
        const topMost = visible.reduce((a, b) =>
          a.boundingClientRect.top < b.boundingClientRect.top ? a : b
        )
        setActiveSection(topMost.target.id)
      },
      { rootMargin: '-10% 0px -70% 0px', threshold: 0 }
    )

    elements.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [loading, sectionIds])

  const fieldLabels = getFieldLabels(form.erp_type, t)

  const isSaveDisabled =
    saving ||
    (isSuperAdmin && !form.erp_name) ||
    (isSuperAdmin && form.erp_type !== 'csv' && !form.base_url) ||
    (isSuperAdmin && form.erp_type === 'salesforce' && (!form.api_key || !form.api_secret)) ||
    (isSuperAdmin &&
      form.erp_type === 'business_central' &&
      (!form.bc_tenant_id || !form.api_secret)) ||
    (isSuperAdmin && form.erp_type === 'bleez' && (!form.siren || !form.siret))

  // ERP admins have no list to return to — send them to the dashboard instead
  const backUrl = isSuperAdmin ? '/admin/erps' : '/admin'

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="flex gap-8 max-w-6xl mx-auto animate-fade-in">
      {/* ── Sidebar nav ── */}
      <aside className="hidden lg:block w-56 flex-shrink-0">
        <div className="sticky top-6">
          {isSuperAdmin && (
            <button
              onClick={() => navigate(backUrl)}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('admin.erps.backToList')}
            </button>
          )}
          <nav className="space-y-1">
            {sections.map((sec) => (
              <button
                key={sec.id}
                onClick={() => scrollToSection(sec.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  activeSection === sec.id
                    ? 'bg-primary-50 text-gray-900 dark:bg-primary-900 dark:text-primary-200'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <sec.icon
                  className={`w-4 h-4 ${activeSection === sec.id ? 'text-primary-600 dark:text-primary-300' : 'text-gray-400 dark:text-gray-500'}`}
                />
                {sec.label}
                {activeSection === sec.id && (
                  <ChevronRight className="w-3 h-3 ml-auto text-primary-500" />
                )}
              </button>
            ))}
          </nav>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="flex-1 min-w-0 space-y-6 pb-24">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            {isSuperAdmin && (
              <button
                onClick={() => navigate(backUrl)}
                className="lg:hidden flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-3 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('admin.erps.backToList')}
              </button>
            )}
            <h1 className="text-2xl font-bold text-gray-900">
              {editingId ? t('admin.erps.pageEdit') : t('admin.erps.pageCreate')}
            </h1>
            {form.erp_name && <p className="text-sm text-gray-500 mt-1">{form.erp_name}</p>}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {editingId && (
              <button
                onClick={handleTest}
                className="flex items-center gap-2 px-4 py-2 text-blue-700 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors text-sm font-medium"
              >
                <Wifi className="w-4 h-4" />
                {t('admin.erps.testConnection')}
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={isSaveDisabled}
              className="flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-start gap-3">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {error}
          </div>
        )}

        {!isSuperAdmin && (
          <div className="flex items-start gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-blue-500" />
            <p>
              {t(
                'admin.erps.erpAdminNotice',
                'Vous pouvez modifier la description, le logo, le site web, la boutique, les modules activés et le chatbot. Les paramètres de connexion ERP, de facturation et de téléphonie sont gérés par le super-administrateur.'
              )}
            </p>
          </div>
        )}

        {/* ── SECTION: Informations générales ── */}
        <Section
          id="general"
          icon={Building2}
          title={t('admin.erps.sectionGeneral')}
          subtitle={t('admin.erps.sectionGeneralSubtitle')}
        >
          <div className="grid grid-cols-2 gap-5">
            <Field label={t('admin.erps.fieldType')} required={isSuperAdmin}>
              <select
                value={form.erp_type}
                onChange={(e) => setForm({ ...form, erp_type: e.target.value })}
                disabled={!isSuperAdmin}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              >
                {ERP_TYPES.map((et) => (
                  <option key={et.value} value={et.value}>
                    {et.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label={t('admin.erps.fieldName')} required={isSuperAdmin}>
              <input
                type="text"
                value={form.erp_name}
                onChange={(e) => setForm({ ...form, erp_name: e.target.value })}
                disabled={!isSuperAdmin}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              />
            </Field>
          </div>

          <Field label={t('admin.erps.fieldDescription')}>
            <textarea
              value={form.erp_description}
              onChange={(e) => setForm({ ...form, erp_description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={2}
            />
          </Field>

          <Field
            label={t('admin.erps.fieldSiren')}
            hint={t('admin.erps.fieldSirenHint')}
            help={t('admin.erps.fieldSirenHelp')}
            required={form.erp_type === 'bleez'}
          >
            <input
              type="text"
              value={form.siren}
              onChange={(e) => setForm({ ...form, siren: e.target.value })}
              maxLength={9}
              placeholder="123456789"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                form.erp_type === 'bleez' && !form.siren
                  ? 'border-red-400 focus:ring-red-400'
                  : 'border-gray-300 focus:ring-primary-500'
              }`}
            />
            {form.siren && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(buildLoginUrl(form.siren))
                    setCopiedSiren(true)
                    setTimeout(() => setCopiedSiren(false), 2000)
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${copiedSiren ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-primary-50 hover:text-primary-700 hover:border-primary-300'}`}
                >
                  {copiedSiren ? (
                    <>
                      <ClipboardCheck className="w-3.5 h-3.5" />
                      {t('admin.erps.loginLinkCopied')}
                    </>
                  ) : (
                    <>
                      <Link2 className="w-3.5 h-3.5" />
                      {t('admin.erps.copyLoginLink')}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(buildShopUrl(form.siren))
                    setCopiedShop(true)
                    setTimeout(() => setCopiedShop(false), 2000)
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${copiedShop ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-orange-50 hover:text-orange-700 hover:border-orange-300'}`}
                >
                  {copiedShop ? (
                    <>
                      <ClipboardCheck className="w-3.5 h-3.5" />
                      {t('admin.erps.shopLinkCopied')}
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="w-3.5 h-3.5" />
                      {t('admin.erps.copyShopLink')}
                    </>
                  )}
                </button>
              </div>
            )}
          </Field>

          <Field label={t('admin.erps.fieldWebsite')} hint={t('admin.erps.fieldWebsiteHint')}>
            <input
              type="url"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://www.example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </Field>

          <Field label={t('admin.erps.fieldLogo')} hint={t('admin.erps.logoHint')}>
            <div className="flex items-center gap-4">
              {logoPreview ? (
                <div className="relative">
                  <img
                    src={logoPreview}
                    alt="Logo"
                    className="h-16 w-16 object-contain rounded-xl border border-gray-200 bg-gray-50"
                  />
                  <button
                    type="button"
                    onClick={() => setLogoPreview(null)}
                    className="absolute -top-2 -right-2 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="h-16 w-16 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                  <ImageIcon className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Upload className="w-4 h-4" />
                {logoPreview ? t('admin.erps.logoChange') : t('admin.erps.logoUpload')}
              </button>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (!file) return
                  if (file.size > 200 * 1024) {
                    setError(t('admin.erps.logoSizeError'))
                    return
                  }
                  const reader = new FileReader()
                  reader.onload = () => setLogoPreview(reader.result as string)
                  reader.readAsDataURL(file)
                  e.target.value = ''
                }}
              />
            </div>
          </Field>

          {isSuperAdmin && (
            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="is_active"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                {t('admin.erps.fieldActive')}
              </label>
            </div>
          )}
        </Section>

        {/* ── SECTION: Connexion ERP ── */}
        <Section
          id="connection"
          icon={Plug}
          title={t('admin.erps.sectionConnection')}
          subtitle={t('admin.erps.sectionConnectionSubtitle')}
        >
          {!isSuperAdmin && (
            <div className="flex items-start gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
              <p>
                {t(
                  'admin.erps.connectionReadOnly',
                  'Les paramètres de connexion ERP sont gérés par le super-administrateur.'
                )}
              </p>
            </div>
          )}

          {/* Connection fields: super-admin only */}
          {isSuperAdmin && (
            <>
              {(() => {
                const meta = ERP_META.find((m) => m.erp_type === form.erp_type)
                if (!meta) return null
                const entry: ErpHelpEntry = {
                  ...meta,
                  tip: t(`adminHelpTooltips.erps.${form.erp_type}.tip`),
                  steps: t(`adminHelpTooltips.erps.${form.erp_type}.steps`, {
                    returnObjects: true,
                  }) as Step[],
                }
                return (
                  <div className="rounded-xl border border-gray-200 overflow-hidden">
                    <ErpAccordionItem
                      entry={entry}
                      defaultOpen={false}
                      officialDocLabel={t('adminHelpTooltips.officialDoc')}
                    />
                  </div>
                )
              })()}

              <Field
                label={fieldLabels.base_url}
                help={fieldLabels.base_url_help}
                required={form.erp_type !== 'csv'}
              >
                <input
                  type="text"
                  value={form.base_url}
                  onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                  placeholder={fieldLabels.base_url_placeholder}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                />
              </Field>

              {(fieldLabels.show_api_key || fieldLabels.show_api_secret) && (
                <div
                  className={`grid gap-4 ${fieldLabels.show_api_key && fieldLabels.show_api_secret ? 'grid-cols-2' : 'grid-cols-1'}`}
                >
                  {fieldLabels.show_api_key && (
                    <Field label={fieldLabels.api_key} help={fieldLabels.api_key_help}>
                      <input
                        type="text"
                        value={form.api_key}
                        onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </Field>
                  )}
                  {fieldLabels.show_api_secret && (
                    <Field label={fieldLabels.api_secret} help={fieldLabels.api_secret_help}>
                      {form.erp_type === 'odoo' ? (
                        <input
                          type="text"
                          value={form.api_secret}
                          onChange={(e) => setForm({ ...form, api_secret: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      ) : (
                        <PasswordInput
                          value={form.api_secret}
                          onChange={(e) => setForm({ ...form, api_secret: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      )}
                    </Field>
                  )}
                </div>
              )}

              {fieldLabels.show_api_version && (
                <Field label={fieldLabels.api_version} help={fieldLabels.api_version_help}>
                  <input
                    type="text"
                    value={form.api_version}
                    onChange={(e) => setForm({ ...form, api_version: e.target.value })}
                    placeholder={form.erp_type === 'salesforce' ? 'v59.0' : ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </Field>
              )}

              {form.erp_type === 'bleez' && (
                <div className="border-t border-gray-200 pt-4">
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    {t('admin.erps.fields.bleez.oauth2Title')}
                  </label>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('admin.erps.fields.bleez.bleez_username')}
                      </label>
                      <input
                        type="text"
                        value={form.bleez_username}
                        onChange={(e) => setForm({ ...form, bleez_username: e.target.value })}
                        placeholder="user@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('admin.erps.fields.bleez.bleez_password')}
                      </label>
                      <PasswordInput
                        value={form.bleez_password}
                        onChange={(e) => setForm({ ...form, bleez_password: e.target.value })}
                        placeholder="••••••••"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('admin.erps.fields.bleez.bleez_cle_encryptee')}
                        <HelpTip text={t('admin.erps.fields.bleez.bleez_cle_encryptee_help')} />
                      </label>
                      <input
                        type="text"
                        value={form.bleez_cle_encryptee}
                        onChange={(e) => setForm({ ...form, bleez_cle_encryptee: e.target.value })}
                        placeholder="votre_cle_encryptee"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              )}

              {form.erp_type === 'business_central' && (
                <div className="border-t border-gray-200 pt-4 space-y-4">
                  <p className="text-sm font-semibold text-gray-900">
                    {t('admin.erps.fields.business_central.azureTitle')}
                  </p>
                  <Field label={t('admin.erps.fields.business_central.bc_tenant_id')}>
                    <input
                      type="text"
                      value={form.bc_tenant_id}
                      onChange={(e) => setForm({ ...form, bc_tenant_id: e.target.value })}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label={t('admin.erps.fields.business_central.bc_environment')}>
                      <select
                        value={form.bc_environment}
                        onChange={(e) => setForm({ ...form, bc_environment: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="production">Production</option>
                        <option value="sandbox">Sandbox</option>
                      </select>
                    </Field>
                    <Field
                      label={t('admin.erps.fields.business_central.bc_company_id')}
                      hint={t('admin.erps.fields.business_central.bc_company_id_help')}
                    >
                      <input
                        type="text"
                        value={form.bc_company_id}
                        onChange={(e) => setForm({ ...form, bc_company_id: e.target.value })}
                        placeholder={t('common.optional')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </Field>
                  </div>
                </div>
              )}
            </>
          )}

          {/* STT Vocabulary — accessible to ERP admin */}
          <div className="border-t border-gray-200 pt-4">
            <Field label={t('admin.erps.sttVocabulary')} hint={t('admin.erps.sttVocabularyHint')}>
              <textarea
                value={form.stt_vocabulary}
                onChange={(e) => setForm({ ...form, stt_vocabulary: e.target.value })}
                placeholder={t('admin.erps.sttVocabularyPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                rows={2}
              />
            </Field>
          </div>
        </Section>

        {/* ── SECTION: Facturation électronique — super-admin only ── */}
        {isSuperAdmin && (
          <>
            <Section
              id="einvoice"
              icon={FileText}
              title={t('admin.erps.sectionEinvoiceTitle')}
              subtitle={t('admin.erps.sectionEinvoiceSubtitle')}
            >
              {form.erp_type !== 'bleez' && (!form.siren || !form.siret) && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
                  <p>{t('admin.erps.sirenSiretWarning')}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label={t('admin.erps.fieldSiren')}
                  help={t('admin.erps.fieldSirenHelp')}
                  required={form.erp_type === 'bleez'}
                >
                  <input
                    type="text"
                    value={form.siren}
                    onChange={(e) => setForm({ ...form, siren: e.target.value })}
                    maxLength={9}
                    placeholder="123456789"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 text-sm focus:border-transparent ${
                      form.erp_type === 'bleez' && !form.siren
                        ? 'border-red-400 focus:ring-red-400'
                        : 'border-gray-300 focus:ring-primary-500'
                    }`}
                  />
                </Field>
                <Field
                  label={t('admin.erps.fieldSiret')}
                  help={t('admin.erps.fieldSiretHelp')}
                  required={form.erp_type === 'bleez'}
                >
                  <input
                    type="text"
                    value={form.siret}
                    onChange={(e) => setForm({ ...form, siret: e.target.value })}
                    maxLength={14}
                    placeholder="12345678900012"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 text-sm focus:border-transparent ${
                      form.erp_type === 'bleez' && !form.siret
                        ? 'border-red-400 focus:ring-red-400'
                        : 'border-gray-300 focus:ring-primary-500'
                    }`}
                  />
                </Field>
                <Field
                  label={t('admin.erps.fieldVatNumber')}
                  help={t('admin.erps.fieldVatNumberHelp')}
                >
                  <input
                    type="text"
                    value={form.vat_number}
                    onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                    maxLength={20}
                    placeholder="FR12345678901"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  />
                </Field>
                <Field label={t('admin.erps.fieldIban')} help={t('admin.erps.fieldIbanHelp')}>
                  <input
                    type="text"
                    value={form.iban}
                    onChange={(e) => setForm({ ...form, iban: e.target.value })}
                    maxLength={34}
                    placeholder="FR7612345678901234567890189"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm font-mono"
                  />
                </Field>
                <Field label={t('admin.erps.fieldBic')} help={t('admin.erps.fieldBicHelp')}>
                  <input
                    type="text"
                    value={form.bic}
                    onChange={(e) => setForm({ ...form, bic: e.target.value })}
                    maxLength={11}
                    placeholder="AGRIFRPP"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm font-mono"
                  />
                </Field>
              </div>

              {/* PA Connector */}
              <div className="border-t border-gray-200 pt-4 space-y-4">
                <p className="text-sm font-semibold text-gray-900">
                  {t('admin.erps.sectionPaConnector')}
                </p>
                <Field
                  label={t('admin.erps.fieldPaProvider')}
                  help={t('admin.erps.fieldPaProviderHelp')}
                >
                  <select
                    value={form.pa_provider}
                    onChange={(e) =>
                      setForm({ ...form, pa_provider: e.target.value, pa_config: {} })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  >
                    <option value="">{t('admin.erps.paNone')}</option>
                    {paProviders.map((p) => (
                      <option key={p.name} value={p.name}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </Field>
                {form.pa_provider &&
                  paProviders
                    .filter((p) => p.name === form.pa_provider)
                    .flatMap((p) => p.fields)
                    .map((field) => (
                      <Field key={field.key} label={field.label} required={field.required}>
                        {field.type === 'boolean' ? (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={
                                form.pa_config[field.key] === 'true' ||
                                (field.default === true && form.pa_config[field.key] === undefined)
                              }
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  pa_config: {
                                    ...form.pa_config,
                                    [field.key]: e.target.checked ? 'true' : 'false',
                                  },
                                })
                              }
                              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <span className="text-sm text-gray-600">{field.label}</span>
                          </label>
                        ) : field.type === 'select' && field.options ? (
                          <select
                            value={form.pa_config[field.key] ?? (field.default as string) ?? ''}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                pa_config: { ...form.pa_config, [field.key]: e.target.value },
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                          >
                            {field.options.map((opt: string) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={field.type === 'password' ? 'password' : 'text'}
                            value={form.pa_config[field.key] ?? ''}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                pa_config: { ...form.pa_config, [field.key]: e.target.value },
                              })
                            }
                            placeholder={field.placeholder ?? ''}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                          />
                        )}
                      </Field>
                    ))}

                {form.pa_provider && editingId && (
                  <div className="flex items-center gap-3 pt-1">
                    <button
                      type="button"
                      onClick={handlePATest}
                      disabled={paTestLoading}
                      className="flex items-center gap-2 px-3 py-2 text-blue-700 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors text-sm font-medium disabled:opacity-50"
                    >
                      {paTestLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Wifi className="w-4 h-4" />
                      )}
                      {t('admin.erps.testPaConnection', 'Tester la connexion PA')}
                    </button>
                    {paTestResult && (
                      <span
                        className={`flex items-center gap-1.5 text-sm font-medium ${paTestResult.success ? 'text-green-600' : 'text-red-600'}`}
                      >
                        {paTestResult.success ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          <XCircle className="w-4 h-4" />
                        )}
                        {paTestResult.message}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </Section>
          </>
        )}

        {/* ── SECTION: Boutique & Modules ── */}
        <Section
          id="modules"
          icon={LayoutGrid}
          title={t('admin.erps.sectionModules')}
          subtitle={t('admin.erps.sectionModulesSubtitle')}
        >
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                id="shop_enabled"
                checked={form.shop_enabled}
                onChange={(e) => setForm({ ...form, shop_enabled: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">
                {t('admin.erps.shopEnabled')}
              </span>
            </label>
            {form.shop_enabled && (
              <label className="flex items-center gap-2 cursor-pointer ml-6">
                <input
                  type="checkbox"
                  id="shop_show_prices"
                  checked={form.shop_show_prices}
                  onChange={(e) => setForm({ ...form, shop_show_prices: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">{t('admin.erps.shopShowPrices')}</span>
              </label>
            )}
          </div>

          <div className="border-t border-gray-200 pt-4">
            <p className="text-sm font-semibold text-gray-900 mb-3">
              {t('admin.erps.enabledModules')}
            </p>

            {/* Super-admin: table with test badge + active + enabled columns */}
            {isSuperAdmin ? (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="px-4 py-2 text-left">{t('admin.erps.colModule')}</th>
                      <th className="px-4 py-2 text-left">{t('admin.erps.colLastTest')}</th>
                      <th className="px-4 py-2 text-center">{t('admin.erps.colActivatable')}</th>
                      <th className="px-4 py-2 text-center">{t('admin.erps.colEnabled')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {ALL_MODULES.map((mod) => {
                      const testRes = lastTestModules?.[mod.key]
                      return (
                        <tr key={mod.key} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-700">{t(mod.labelKey)}</td>
                          <td className="px-4 py-3">
                            {!lastTestModules ? (
                              <span className="text-xs text-gray-400">
                                — {t('admin.erps.moduleNotTested')}
                              </span>
                            ) : !testRes ? (
                              <span className="text-xs text-gray-400">
                                — {t('admin.erps.moduleNotTested')}
                              </span>
                            ) : testRes.success ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-700">
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                {t('admin.erps.testOk')}
                                {testRes.count !== undefined ? ` (${testRes.count})` : ''}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-red-600">
                                <XCircle className="w-3.5 h-3.5" />
                                {t('admin.erps.testFail')}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={modulesActive[mod.key] ?? true}
                              onChange={() => {
                                const newVal = !(modulesActive[mod.key] ?? true)
                                setModulesActive((prev) => ({ ...prev, [mod.key]: newVal }))
                                if (!newVal)
                                  setModulesEnabled((prev) => ({ ...prev, [mod.key]: false }))
                              }}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={modulesEnabled[mod.key] ?? false}
                              onChange={() =>
                                setModulesEnabled((prev) => ({
                                  ...prev,
                                  [mod.key]: !prev[mod.key],
                                }))
                              }
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              /* ERP admin: grid with lock on inactive modules */
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {ALL_MODULES.map((mod) => {
                  const active = modulesActive[mod.key] ?? true
                  const enabled = modulesEnabled[mod.key] ?? false
                  return (
                    <label
                      key={mod.key}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                        !active
                          ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                          : enabled
                            ? 'border-primary-300 bg-primary-50 cursor-pointer'
                            : 'border-gray-200 bg-white hover:bg-gray-50 cursor-pointer'
                      }`}
                    >
                      {!active ? (
                        <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      ) : (
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={() =>
                            setModulesEnabled((prev) => ({ ...prev, [mod.key]: !prev[mod.key] }))
                          }
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      )}
                      <span
                        className={`text-sm font-medium ${!active ? 'text-gray-400' : enabled ? 'text-gray-900' : 'text-gray-600'}`}
                      >
                        {t(mod.labelKey)}
                      </span>
                    </label>
                  )
                })}
              </div>
            )}
          </div>
        </Section>

        {/* ── SECTION: Chatbot & Téléphonie ── */}
        <Section
          id="chatbot"
          icon={MessageSquare}
          title={t('admin.erps.sectionChatbot')}
          subtitle={t('admin.erps.sectionChatbotSubtitle')}
        >
          {/* Chatbot */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {t('admin.erps.chatbotTitle')}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{t('admin.erps.chatbotHint')}</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.chatbot_config.enabled}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      chatbot_config: { ...form.chatbot_config, enabled: e.target.checked },
                    })
                  }
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">{t('admin.erps.chatbotEnabled')}</span>
              </label>
            </div>
            {form.chatbot_config.enabled && (
              <div className="space-y-4 pl-4 border-l-2 border-primary-100">
                <Field label={t('admin.erps.chatbotWelcome')}>
                  <textarea
                    value={form.chatbot_config.welcome_message}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        chatbot_config: { ...form.chatbot_config, welcome_message: e.target.value },
                      })
                    }
                    placeholder={t('admin.erps.chatbotWelcomePlaceholder')}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                  />
                </Field>
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">
                    {t('admin.erps.chatbotQuestions')}
                  </p>
                  <div className="space-y-2">
                    {form.chatbot_config.questions.map((q, idx) => (
                      <div key={q.id} className="bg-gray-50 rounded-xl p-3 space-y-2">
                        <div className="flex items-start gap-2">
                          <GripVertical className="w-4 h-4 text-gray-300 mt-1 flex-shrink-0" />
                          <div className="flex-1 space-y-2">
                            <input
                              type="text"
                              value={q.label}
                              onChange={(e) => {
                                const qs = [...form.chatbot_config.questions]
                                qs[idx] = { ...qs[idx], label: e.target.value }
                                setForm({
                                  ...form,
                                  chatbot_config: { ...form.chatbot_config, questions: qs },
                                })
                              }}
                              placeholder={t('admin.erps.chatbotQuestionLabel')}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 text-sm"
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <select
                                value={q.type}
                                onChange={(e) => {
                                  const qs = [...form.chatbot_config.questions]
                                  qs[idx] = {
                                    ...qs[idx],
                                    type: e.target.value as ChatbotQuestion['type'],
                                  }
                                  setForm({
                                    ...form,
                                    chatbot_config: { ...form.chatbot_config, questions: qs },
                                  })
                                }}
                                className="px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 text-sm"
                              >
                                <option value="text">{t('admin.erps.chatbotTypeText')}</option>
                                <option value="textarea">
                                  {t('admin.erps.chatbotTypeTextarea')}
                                </option>
                                <option value="date">{t('admin.erps.chatbotTypeDate')}</option>
                                <option value="time">{t('admin.erps.chatbotTypeTime')}</option>
                                <option value="number">{t('admin.erps.chatbotTypeNumber')}</option>
                                <option value="email">{t('admin.erps.chatbotTypeEmail')}</option>
                                <option value="phone">{t('admin.erps.chatbotTypePhone')}</option>
                                <option value="select">{t('admin.erps.chatbotTypeSelect')}</option>
                              </select>
                              <select
                                value={q.field}
                                onChange={(e) => {
                                  const qs = [...form.chatbot_config.questions]
                                  qs[idx] = {
                                    ...qs[idx],
                                    field: e.target.value as ChatbotQuestion['field'],
                                  }
                                  setForm({
                                    ...form,
                                    chatbot_config: { ...form.chatbot_config, questions: qs },
                                  })
                                }}
                                className="px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 text-sm"
                              >
                                <option value="subject">
                                  {t('admin.erps.chatbotFieldSubject')}
                                </option>
                                <option value="message">
                                  {t('admin.erps.chatbotFieldMessage')}
                                </option>
                                <option value="type_code">
                                  {t('admin.erps.chatbotFieldType')}
                                </option>
                                <option value="category_code">
                                  {t('admin.erps.chatbotFieldCategory')}
                                </option>
                                <option value="severity_code">
                                  {t('admin.erps.chatbotFieldSeverity')}
                                </option>
                              </select>
                            </div>
                            {q.type === 'select' && (
                              <input
                                type="text"
                                value={(q.options ?? []).join(', ')}
                                onChange={(e) => {
                                  const qs = [...form.chatbot_config.questions]
                                  qs[idx] = {
                                    ...qs[idx],
                                    options: e.target.value
                                      .split(',')
                                      .map((s) => s.trim())
                                      .filter(Boolean),
                                  }
                                  setForm({
                                    ...form,
                                    chatbot_config: { ...form.chatbot_config, questions: qs },
                                  })
                                }}
                                placeholder={t('admin.erps.chatbotOptionsPlaceholder')}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 text-sm"
                              />
                            )}
                            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={q.required}
                                onChange={(e) => {
                                  const qs = [...form.chatbot_config.questions]
                                  qs[idx] = { ...qs[idx], required: e.target.checked }
                                  setForm({
                                    ...form,
                                    chatbot_config: { ...form.chatbot_config, questions: qs },
                                  })
                                }}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              {t('admin.erps.chatbotRequired')}
                            </label>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const qs = form.chatbot_config.questions.filter((_, i) => i !== idx)
                              setForm({
                                ...form,
                                chatbot_config: { ...form.chatbot_config, questions: qs },
                              })
                            }}
                            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newQ: ChatbotQuestion = {
                        id: Date.now().toString(),
                        label: '',
                        type: 'text',
                        required: false,
                        field: 'message',
                      }
                      setForm({
                        ...form,
                        chatbot_config: {
                          ...form.chatbot_config,
                          questions: [...form.chatbot_config.questions, newQ],
                        },
                      })
                    }}
                    className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 text-gray-500 rounded-xl hover:border-primary-400 hover:text-primary-600 text-sm transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    {t('admin.erps.chatbotAddQuestion')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Vapi — super-admin only */}
          {isSuperAdmin && (
            <div className="border-t border-gray-200 pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-purple-600" />
                  <p className="text-sm font-semibold text-gray-900">{t('admin.erps.vapiTitle')}</p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.vapi_config.enabled}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        vapi_config: { ...form.vapi_config, enabled: e.target.checked },
                      })
                    }
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">{t('admin.erps.vapiEnabled')}</span>
                </label>
              </div>
              <p className="text-xs text-gray-500">{t('admin.erps.vapiHint')}</p>
              {form.vapi_config.enabled && (
                <div className="space-y-3 pl-4 border-l-2 border-purple-100">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t('admin.erps.vapiLanguage')}>
                      <select
                        value={form.vapi_config.language}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            vapi_config: { ...form.vapi_config, language: e.target.value },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                      >
                        <option value="fr">Français (fr)</option>
                        <option value="en">English (en)</option>
                      </select>
                    </Field>
                    <Field label={t('admin.erps.vapiVoiceProvider')}>
                      <select
                        value={form.vapi_config.voice_provider}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            vapi_config: { ...form.vapi_config, voice_provider: e.target.value },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                      >
                        <option value="azure">Azure (Microsoft)</option>
                        <option value="11labs">ElevenLabs</option>
                        <option value="openai">OpenAI</option>
                        <option value="deepgram">Deepgram</option>
                      </select>
                    </Field>
                  </div>
                  <Field label={t('admin.erps.vapiVoiceId')}>
                    <input
                      type="text"
                      value={form.vapi_config.voice_id}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          vapi_config: { ...form.vapi_config, voice_id: e.target.value },
                        })
                      }
                      placeholder={
                        form.vapi_config.voice_provider === 'azure'
                          ? form.vapi_config.language === 'fr'
                            ? 'fr-FR-DeniseNeural'
                            : 'en-US-JennyNeural'
                          : 'voice-id'
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                    />
                  </Field>
                  <Field label={t('admin.erps.vapiPhoneNumber')}>
                    {vapiPhoneNumbers.length > 0 ? (
                      <select
                        value={form.vapi_config.phone_number_id}
                        onChange={(e) => {
                          const sel = vapiPhoneNumbers.find((p) => p.id === e.target.value)
                          setForm({
                            ...form,
                            vapi_config: {
                              ...form.vapi_config,
                              phone_number_id: e.target.value,
                              phone_number: sel?.number ?? '',
                            },
                          })
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                      >
                        <option value="">{t('admin.erps.vapiPhoneSelect')}</option>
                        {vapiPhoneNumbers.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} — {p.number}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={form.vapi_config.phone_number}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            vapi_config: { ...form.vapi_config, phone_number: e.target.value },
                          })
                        }
                        placeholder="+33XXXXXXXXX"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                      />
                    )}
                  </Field>
                  {form.vapi_config.assistant_id && (
                    <Field label={t('admin.erps.vapiAssistantId')}>
                      <input
                        type="text"
                        value={form.vapi_config.assistant_id}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm font-mono"
                      />
                    </Field>
                  )}
                  {editingId && (
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleVapiSync}
                        disabled={vapiSyncing}
                        className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm disabled:opacity-50"
                      >
                        {vapiSyncing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        {t('admin.erps.vapiSync')}
                      </button>
                      {vapiSyncResult && (
                        <span
                          className={`text-sm ${vapiSyncResult.success ? 'text-green-600' : 'text-red-600'}`}
                        >
                          {vapiSyncResult.message}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Section>

        {/* ── SECTION: Paiement — super-admin only ── */}
        {isSuperAdmin && (
          <>
            <Section
              id="payment"
              icon={CreditCard}
              title={t('admin.erps.sectionPayment')}
              subtitle={t('admin.erps.sectionPaymentSubtitle')}
            >
              {editingId ? (
                <StripeConfigSection erpId={editingId} isSuperAdmin={isSuperAdmin} />
              ) : (
                <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {t('admin.erps.stripeSaveFirst')}
                </div>
              )}
            </Section>
          </>
        )}

        {/* ── SECTION: Webhooks ERP — visible to admin ERP and super_admin ── */}
        {editingId && (
          <Section
            id="webhooks"
            icon={Webhook}
            title={t('webhookConfig.sectionTitle')}
            subtitle={t('webhookConfig.sectionSubtitle')}
          >
            <WebhookConfigSection erpId={editingId} />
          </Section>
        )}

        {/* Sticky save bar (mobile) */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between gap-3 lg:hidden z-40">
          <button
            onClick={() => navigate(backUrl)}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 text-sm font-medium"
          >
            {t('common.cancel')}
          </button>
          <div className="flex items-center gap-2">
            {editingId && (
              <button
                onClick={handleTest}
                className="flex items-center gap-2 px-4 py-2 text-blue-700 bg-blue-50 rounded-xl text-sm font-medium"
              >
                <Wifi className="w-4 h-4" />
                {t('admin.erps.testConnection')}
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={isSaveDisabled}
              className="flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-xl text-sm font-medium disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </div>

      {/* ── Test Connection Modal ── */}
      {testOpen && (
        <div className="fixed inset-0 bg-gray-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Wifi className="w-5 h-5 text-blue-600" />
                {t('admin.erps.testModalTitle', { name: form.erp_name })}
              </h2>
              <button
                onClick={() => setTestOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-xl"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {testLoading ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3 text-gray-500">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  <span className="text-sm">{t('admin.erps.testTesting')}</span>
                </div>
              ) : testResult ? (
                <>
                  <div
                    className={`flex items-center gap-3 p-3 rounded-xl ${testResult.success ? 'bg-green-50' : 'bg-red-50'}`}
                  >
                    {testResult.success ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    )}
                    <div>
                      <p
                        className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}
                      >
                        {t('admin.erps.testConnectionStatus')}
                      </p>
                      <p
                        className={`text-xs mt-0.5 ${testResult.success ? 'text-green-700' : 'text-red-700'}`}
                      >
                        {testResult.success
                          ? t('admin.erps.connectionSuccess')
                          : (testResult.error ?? t('admin.erps.connectionFail'))}
                      </p>
                    </div>
                  </div>
                  {testResult.modules && Object.keys(testResult.modules).length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        {t('admin.erps.testModuleAccess')}
                      </p>
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                              <th className="px-4 py-2">{t('admin.erps.testModuleCol')}</th>
                              <th className="px-4 py-2">{t('admin.erps.testStatusCol')}</th>
                              <th className="px-4 py-2 text-right">
                                {t('admin.erps.testCountCol')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {Object.entries(testResult.modules).map(([mod, res]) => (
                              <tr key={mod} className="hover:bg-gray-50">
                                <td className="px-4 py-2.5 font-medium text-gray-700">
                                  {t(`nav.${mod}`, { defaultValue: mod })}
                                </td>
                                <td className="px-4 py-2.5">
                                  {res.success ? (
                                    <span className="inline-flex items-center gap-1 text-green-700">
                                      <CheckCircle2 className="w-4 h-4" />
                                      {t('admin.erps.testOk')}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-red-600">
                                      <XCircle className="w-4 h-4" />
                                      {t('admin.erps.testFail')}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2.5 text-right text-gray-500">
                                  {res.success && res.count !== undefined ? (
                                    t('admin.erps.testModuleCount', { count: res.count })
                                  ) : res.error ? (
                                    <span className="text-xs text-red-500">{res.error}</span>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
            <div className="flex justify-end p-5 border-t border-gray-200">
              <button
                onClick={() => setTestOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 text-sm"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
